import {clamp, head, last} from 'lodash-es';
import {findReversed} from '@dovca/toolbox';
import type {Position} from '@vueuse/core';
import type {CSSProperties} from 'vue';
import {useDrag, usePinch, useWheel} from '@vueuse/gesture';
import type {Size, UseMapControlsOptions, ZoomMultiplier} from '~/types/UseMapControls';

// ==================== CONSTANTS ====================

const DEFAULT_SENSITIVITY = 0.5;
const SENSITIVITY_RANGE = {
	min: 0.0005, // slowest
	max: 0.005, // fastest
};
const TRANSITION_PROPERTY_DEFAULT = 'transform 300ms';

/**
 * Composable for managing map zoom, pan, and gesture controls.
 *
 * This composable provides functionality to handle zooming (in/out), panning, and
 * responsive zooming via mouse wheel, pinch gestures, and drag gestures.
 * It ensures smooth transitions and respects the parent container boundaries.
 *
 * @param target - A reference to the DOM element.
 * @param options - Configuration options.
 * @returns An object with methods to control zoom, pan, and styling.
 */
export const useMapControls = (
	target: MaybeRefOrGetter<HTMLElement | undefined | null>,
	{steps, sensitivity, onZoom, fill}: UseMapControlsOptions,
) => {
	// ==================== ELEMENT REFERENCES & SIZES ====================

	const originalTargetSize = useElementSize(target);
	const targetParent = useParentElement(target);
	const targetParentSize = useElementSize(targetParent);

	const currentTargetSize = computed<Size>(() => ({
		width: originalTargetSize.width.value * zoom.value,
		height: originalTargetSize.height.value * zoom.value,
	}));

	// ==================== ZOOM STATE ====================

	const resolvedSteps = computed(() => unref(steps));

	const defaultStep = computed<ZoomMultiplier>(() => {
		const defaultStep = resolvedSteps.value.find((s) => s.default);
		return defaultStep ?? head(resolvedSteps.value)!;
	});

	const zoom = ref<number>(defaultStep.value.scale);

	const minZoom = computed<number>(() => {
		const idealMinZoom = head(resolvedSteps.value)?.scale ?? 1;

		if (!fill || !targetParent.value) {
			return idealMinZoom;
		}

		const minZoomToSize = Math.max(targetParentSize.width.value / originalTargetSize.width.value, targetParentSize.height.value / originalTargetSize.height.value);
		return clamp(minZoomToSize, idealMinZoom, Infinity);
	});

	const maxZoom = computed<number>(() => last(resolvedSteps.value)?.scale ?? 1);
	const canZoomIn = computed<boolean>(() => zoom.value < maxZoom.value);
	const canZoomOut = computed<boolean>(() => zoom.value > minZoom.value);

	const zoomSensitivity = computed<number>(() => {
		const clamped = clamp(sensitivity ?? DEFAULT_SENSITIVITY, 0, 1);
		return SENSITIVITY_RANGE.min + (clamped * (SENSITIVITY_RANGE.max - SENSITIVITY_RANGE.min));
	});

	let accumulatedDelta = 0;
	let rafId: number | null = null;

	// ==================== PAN STATE ====================

	const panRatio = ref<Position>(computePanRatioFromScale(defaultStep.value.scale));

	const currentMapOffset = computed<Position>(() => ({
		x: targetParentSize.width.value * panRatio.value.x,
		y: targetParentSize.height.value * panRatio.value.y,
	}));

	// ==================== POINTER & GESTURE STATE ====================

	const pointer = usePointer();

	const pointerPositionRelativeToTarget = computed(() => {
		const el = toValue(targetParent);

		if (!el) {
			throw new Error('Cannot find target');
		}

		return convertFixedToParentRelativePosition({x: pointer.x.value, y: pointer.y.value}, el as HTMLElement);
	});

	const transitionStyleValue = ref<string | undefined>();
	let resetTransitionTimeout: NodeJS.Timeout | null = null;

	// ==================== ZOOM FUNCTIONS ====================

	function zoomStep(inOrOut: 1 | -1): void {
		enableTransition(true);
		if (inOrOut === 1 && canZoomIn.value) {
			const step = resolvedSteps.value.find((step) => step.scale > zoom.value);

			if (!step) {
				return;
			}

			updateZoom(step.scale);
			const newPanRatio = computePanRatioFromScale(step.scale) ?? panRatio.value;
			updatePanRatio(newPanRatio);
		} else if (inOrOut === -1 && canZoomOut.value) {
			const step = findReversed<ZoomMultiplier>((step) => step.scale < zoom.value)(resolvedSteps.value);

			if (!step) {
				return;
			}

			updateZoom(step.scale);
			const newPanRatio = computePanRatioFromScale(step.scale) ?? panRatio.value;
			updatePanRatio(newPanRatio);
		}

		onZoom?.();
	}

	function zoomDelta(deltaY: number): void {
		// Accumulate all deltas
		accumulatedDelta += deltaY;

		// Schedule update on next frame (automatically throttled to 60fps)
		if (rafId === null) {
			rafId = requestAnimationFrame(processZoom);
		}
	}

	function processZoom(): void {
		if (!targetParent.value) {
			return;
		}

		rafId = null;

		if (Math.abs(accumulatedDelta) < 1) {
			accumulatedDelta = 0;
			return;
		}

		const zoomDelta = -accumulatedDelta * zoomSensitivity.value;
		const newPossibleZoom = zoom.value + zoomDelta;

		// Store old zoom before changing it
		const oldZoom = zoom.value;
		updateZoom(newPossibleZoom);

		// Calculate the actual zoom change that occurred (after clamping)
		const actualZoomChange = zoom.value - oldZoom;

		// Calculate cursor position as a ratio (0 = left edge, 1 = right edge)
		const cursorXRatio = pointerPositionRelativeToTarget.value.x / targetParentSize.width.value;
		const cursorYRatio = pointerPositionRelativeToTarget.value.y / targetParentSize.height.value;

		// Adjust position so the point under cursor stays in place
		if (zoom.value > 1) {
			updatePanRatio({
				x: panRatio.value.x - cursorXRatio * actualZoomChange,
				y: panRatio.value.y - cursorYRatio * actualZoomChange,
			});
		} else {
			const newPanRatio = computePanRatioFromScale(defaultStep.value.scale);
			updatePanRatio(newPanRatio);
		}

		// Reset accumulator
		accumulatedDelta = 0;

		onZoom?.();
	}

	// ==================== GESTURE HANDLERS ====================

	const drag = useDrag(
		({delta: [deltaX, deltaY], first, touches, event}) => {
			event.preventDefault();

			if (touches > 1) {
				return;
			}

			if (first) {
				transitionStyleValue.value = undefined;
				if (resetTransitionTimeout) {
					clearTimeout(resetTransitionTimeout);
				}
			}

			updatePanRatio({
				x: (currentMapOffset.value.x + deltaX) / targetParentSize.width.value,
				y: (currentMapOffset.value.y + deltaY) / targetParentSize.height.value,
			});
		},
		{
			domTarget: target,
			eventOptions: {passive: false},
			preventWindowScrollY: true,
		},
	);

	const wheel = useWheel(
		({event, delta: [_, deltaY]}) => {
			event.preventDefault();
			zoomDelta(deltaY);
		},
		{
			domTarget: targetParent,
			eventOptions: {passive: false},
		},
	);

	const pinch = usePinch(
		({delta: [deltaX], event}) => {
			event?.preventDefault();
			const deltaBoost = 2;
			zoomDelta(-deltaX * deltaBoost);
		},
		{
			domTarget: targetParent,
			eventOptions: {passive: false},
		},
	);

	function resetGestures() {
		// destroy
		drag.clean();
		wheel.clean();
		pinch.clean();

		// setup
		drag.bind();
		wheel.bind();
		pinch.bind();
	}

	// ==================== STYLES ====================

	const styles = computed<CSSProperties>(() => ({
		position: 'absolute',
		cursor: 'grab',
		width: `${targetParentSize.width.value}px`,
		transform: `translate(${currentMapOffset.value.x}px, ${currentMapOffset.value.y}px) scale(${zoom.value})`,
		transformOrigin: 'top left',
		transition: transitionStyleValue.value,
		willChange: 'transform',
	}));

	// ==================== UTILITY FUNCTIONS ====================

	function clampPanRatio({x, y}: Position) {
		if (!targetParent.value) {
			return {x, y};
		}

		const minPanX = (targetParentSize.width.value - currentTargetSize.value.width) / targetParentSize.width.value;
		const minPanY = (targetParentSize.height.value - currentTargetSize.value.height) / targetParentSize.height.value;

		return {
			x: clamp(x, minPanX, 0),
			y: clamp(y, minPanY, 0),
		};
	}

	function updatePanRatio(pos: Position) {
		panRatio.value = clampPanRatio(pos);
	}

	function updateZoom(newZoom: number) {
		zoom.value = clamp(newZoom, minZoom.value, maxZoom.value);
	}

	function computePanRatioFromScale(scale: number): Position {
		const minScale = scale < minZoom.value ? minZoom.value : scale;
		const panRatio = (-0.5 * (minScale - 1));

		return {
			x: panRatio,
			y: panRatio,
		};
	}

	function convertFixedToParentRelativePosition(position: Position, parent: MaybeRefOrGetter<HTMLElement>): Position {
		const rect = toValue(parent).getBoundingClientRect();

		return {
			x: position.x - rect.left,
			y: position.y - rect.top,
		};
	}

	function enableTransition(transition: boolean) {
		if (resetTransitionTimeout) {
			clearTimeout(resetTransitionTimeout);
		}

		if (transition) {
			transitionStyleValue.value = TRANSITION_PROPERTY_DEFAULT;
		} else {
			resetTransitionTimeout = setTimeout(() => {
				transitionStyleValue.value = undefined;
			}, 300);
		}
	}

	function reset() {
		enableTransition(true);
		updateZoom(defaultStep.value.scale);
		const newPanRatio = computePanRatioFromScale(defaultStep.value.scale);
		updatePanRatio(newPanRatio);
		resetGestures();
	}

	return {
		zoom: readonly(zoom),
		panRatio: readonly(panRatio),
		canZoomIn: readonly(canZoomIn),
		canZoomOut: readonly(canZoomOut),
		styles: readonly(styles),
		zoomStep,
		reset,
	};
};
